/**
 * Usuarios: búsqueda (con espera, para no llenar la bitácora mientras se escribe),
 * filtros y orden en la URL, paginación y exportación CSV auditada.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import type { AdminEstadoUsuario, AdminFiltroUsuarios, PlanId } from '@eco/shared'
import { errorText, useApp, useToast } from '../../components/AppContext'
import { ErrorBox, Skeleton } from '../../components/ui'
import { compacto, haceCuanto } from '../../lib/adminFormat'
import { formatDate } from '../../lib/format'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { EstadoUsuario, Paginador, PLAN_LABEL } from './common'

const LIMITE = 50

export function Usuarios() {
  useDocumentTitle('Usuarios · Administración')
  const { api } = useApp()
  const toast = useToast()
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const filtro: AdminFiltroUsuarios = {
    q: params.get('q') || undefined,
    plan: (params.get('plan') as PlanId) || undefined,
    estado: (params.get('estado') as AdminEstadoUsuario) || undefined,
    orden: (params.get('orden') as AdminFiltroUsuarios['orden']) || 'recientes',
    limite: LIMITE,
    desplazamiento: Number(params.get('desde') ?? 0) || 0
  }
  const [texto, setTexto] = useState(filtro.q ?? '')
  const [exportando, setExportando] = useState(false)

  // La búsqueda espera a que se deje de escribir
  useEffect(() => {
    const t = setTimeout(() => {
      if ((texto.trim() || undefined) !== filtro.q) poner({ q: texto.trim() || null, desde: null })
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto])

  // Si la búsqueda cambia desde fuera (la pestaña «Usuarios», atrás), la caja la sigue
  const qUrl = params.get('q') ?? ''
  useEffect(() => {
    setTexto((t) => (t.trim() === qUrl ? t : qUrl))
  }, [qUrl])

  /** Parte de la URL vigente (no de la de este render): un filtro recién elegido no se pierde. */
  function poner(cambios: Record<string, string | null>) {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(cambios)) {
          if (v === null || v === '') p.delete(k)
          else p.set(k, v)
        }
        return p
      },
      { replace: true }
    )
  }

  const clave = JSON.stringify(filtro)
  const lista = useAsync(() => api.adminUsuarios(filtro), [api, clave])

  async function exportar() {
    setExportando(true)
    try {
      await api.adminExportarUsuarios({ ...filtro, limite: undefined, desplazamiento: undefined })
      toast('Exportación lista (queda registrada en la auditoría)')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setExportando(false)
    }
  }

  return (
    <div className="stack">
      <div className="admin-toolbar" role="search">
        <input
          type="search"
          placeholder="Buscar por correo o nombre"
          aria-label="Buscar usuarios por correo o nombre"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <select
          aria-label="Plan"
          value={filtro.plan ?? ''}
          onChange={(e) => poner({ plan: e.target.value, desde: null })}
        >
          <option value="">Todos los planes</option>
          <option value="free">Gratis</option>
          <option value="personal">Personal</option>
          <option value="equipo">Equipo</option>
        </select>
        <select
          aria-label="Estado"
          value={filtro.estado ?? ''}
          onChange={(e) => poner({ estado: e.target.value, desde: null })}
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activos (30 días)</option>
          <option value="inactivo">Inactivos</option>
          <option value="sin_activar">Sin activar</option>
          <option value="suspendido">Suspendidos</option>
        </select>
        <select
          aria-label="Orden"
          value={filtro.orden}
          onChange={(e) => poner({ orden: e.target.value, desde: null })}
        >
          <option value="recientes">Más recientes</option>
          <option value="actividad">Última actividad</option>
          <option value="reuniones">Más reuniones</option>
          <option value="correo">Por correo</option>
        </select>
        <button className="btn" onClick={() => void exportar()} disabled={exportando}>
          {exportando ? 'Exportando…' : 'Exportar CSV'}
        </button>
      </div>

      {lista.error && <ErrorBox error={lista.error} onRetry={() => void lista.reload()} />}
      {!lista.data && !lista.error && <Skeleton h={220} />}
      {lista.data && lista.data.items.length === 0 && (
        <div className="empty">
          <h2>Sin resultados</h2>
          <p className="muted">Prueba con otro correo o quita los filtros.</p>
        </div>
      )}
      {lista.data && lista.data.items.length > 0 && (
        <div className="card tight" style={{ overflowX: 'auto' }}>
          <table className="simple clic">
            <caption className="sr">Usuarios de eco</caption>
            <thead>
              <tr>
                <th scope="col">Persona</th>
                <th scope="col">Estado</th>
                <th scope="col">Plan</th>
                <th scope="col" className="num">
                  Reuniones
                </th>
                <th scope="col" className="num">
                  Horas
                </th>
                <th scope="col">Última reunión</th>
                <th scope="col">Registro</th>
                <th scope="col">Fuente</th>
              </tr>
            </thead>
            <tbody>
              {lista.data.items.map((u) => (
                <tr key={u.id} onClick={() => nav(`/admin/usuarios/${u.id}`)}>
                  <td>
                    {/* El enlace es lo que se enfoca y anuncia; la fila entera es un atajo del mouse */}
                    <Link
                      to={`/admin/usuarios/${u.id}`}
                      style={{ fontWeight: 550, color: 'inherit' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {u.email}
                    </Link>
                    {u.nombre && <div className="faint">{u.nombre}</div>}
                  </td>
                  <td>
                    <EstadoUsuario estado={u.estado} />
                    {u.esAdmin && (
                      <span className="pill" style={{ marginLeft: 6 }}>
                        Operador
                      </span>
                    )}
                  </td>
                  <td>
                    {PLAN_LABEL[u.plan]}
                    {u.proveedor === 'manual' && <span className="faint"> · cortesía</span>}
                    {u.equipos > 0 && (
                      <div className="faint">
                        {u.equipos} equipo{u.equipos === 1 ? '' : 's'}
                      </div>
                    )}
                  </td>
                  <td className="num">{u.reuniones}</td>
                  <td className="num">{compacto(Math.round((u.minutos / 60) * 10) / 10)}</td>
                  <td>{haceCuanto(u.ultimaReunion)}</td>
                  <td>{formatDate(u.creadoEn)}</td>
                  <td className="faint">{u.fuente ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {lista.data && (
        <Paginador
          desde={filtro.desplazamiento ?? 0}
          cantidad={lista.data.items.length}
          total={lista.data.total}
          limite={LIMITE}
          onCambiar={(d) => poner({ desde: d ? String(d) : null })}
        />
      )}
    </div>
  )
}
